import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faEye, faSpinner, faPlus } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-hot-toast';
import api from '../utils/api';
import PropTypes from 'prop-types';

const BlogManagement = ({ darkMode, onEditPost }) => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteConfirmation, setDeleteConfirmation] = useState({
        show: false,
        postId: null,
        postTitle: '',
        isDeleting: false
    });

    useEffect(() => {
        fetchUserPosts();
    }, []);

    const fetchUserPosts = async () => {
        try {
            setLoading(true);
            const response = await api.get('/blog/author/posts');
            
            if (response.data.success) {
                setPosts(response.data.posts);
            }
        } catch (error) {
            console.error('Error fetching user posts:', error);
            toast.error('Failed to load your blog posts');
        } finally {
            setLoading(false);
        }
    };

    const handleDeletePost = async (postId) => {
        const post = posts.find(p => p._id === postId);
        setDeleteConfirmation({
            show: true,
            postId,
            postTitle: post.title,
            isDeleting: false
        });
    };

    const confirmDelete = async () => {
        setDeleteConfirmation(prev => ({ ...prev, isDeleting: true }));
        try {
            await api.delete(`/blog/${deleteConfirmation.postId}`);
            setPosts(prev => prev.filter(post => post._id !== deleteConfirmation.postId));
            setDeleteConfirmation({ show: false, postId: null, postTitle: '', isDeleting: false });
            toast.success('Blog post deleted successfully!');
        } catch (error) {
            console.error('Delete error:', error);
            toast.error('Failed to delete blog post');
            setDeleteConfirmation(prev => ({ ...prev, isDeleting: false }));
        }
    };

    const handleViewPost = (postId) => {
        window.open(`/single-blog-view/${postId}`, '_blank');
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const truncateText = (text, maxLength) => {
        if (!text || typeof text !== 'string') return '';
        if (text.length <= maxLength) return text;
        return text.substr(0, maxLength).trim() + '...';
    };

    const getPostDescription = (post) => {
        // Handle new format with descriptions array
        if (post.descriptions && Array.isArray(post.descriptions) && post.descriptions.length > 0) {
            return post.descriptions[0].text || '';
        }
        // Handle old format with single description
        return post.description || '';
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <h2 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Manage Blog Posts
                </h2>
            </div>

            {/* Content Container */}
            <div className={`rounded-2xl border p-6 ${
                darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            } shadow-lg`}>
                <div className="space-y-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <FontAwesomeIcon icon={faSpinner} className="animate-spin text-2xl mr-3" />
                            <span>Loading your blog posts...</span>
                        </div>
                    ) : posts.length === 0 ? (
                        <div className="text-center py-12">
                            <div className="mb-4">
                                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-medium mb-2">No blog posts yet</h3>
                            <p className="text-gray-500 mb-4">Create your first blog post to get started!</p>
                            <button
                                onClick={() => {
                                    // Trigger create blog post
                                }}
                                className={`px-4 py-2 rounded-lg font-medium text-white ${
                                    darkMode 
                                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500' 
                                        : 'bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600'
                                } transition-all flex items-center gap-2`}
                            >
                                <FontAwesomeIcon icon={faPlus} />
                                Create Blog Post
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {posts.map((post) => (
                                <div
                                    key={post._id}
                                    className={`p-4 rounded-lg border ${
                                        darkMode 
                                            ? 'bg-gray-700/50 border-gray-600' 
                                            : 'bg-white border-gray-200'
                                    } hover:shadow-lg transition-shadow`}
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-2">
                                                <h3 className="text-lg font-semibold">{post.title}</h3>
                                                <span className={`px-2 py-1 text-xs rounded-full ${
                                                    post.published 
                                                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                                }`}>
                                                    {post.published ? 'Published' : 'Draft'}
                                                </span>
                                            </div>
                                            <p className="text-gray-600 dark:text-gray-300 mb-2">
                                                {truncateText(getPostDescription(post), 150)}
                                            </p>
                                            <div className="flex items-center gap-4 text-sm text-gray-500">
                                                <span>{post.category}</span>
                                                <span>{post.readingTime}</span>
                                                <span>{formatDate(post.createdAt)}</span>
                                                <span>{post.views} views</span>
                                                <span>{post.likes} likes</span>
                                            </div>
                                        </div>
                                        {post.image && (
                                            <img 
                                                src={post.image} 
                                                alt={post.title}
                                                className="w-20 h-20 object-cover rounded-lg ml-4"
                                            />
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-600/30">
                                        <button
                                            onClick={() => handleViewPost(post._id)}
                                            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                                                darkMode 
                                                    ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                                                    : 'bg-blue-500 hover:bg-blue-600 text-white'
                                            } transition-colors flex items-center gap-1`}
                                        >
                                            <FontAwesomeIcon icon={faEye} />
                                            View
                                        </button>
                                        <button
                                            onClick={() => onEditPost && onEditPost(post)}
                                            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                                                darkMode 
                                                    ? 'bg-green-600 hover:bg-green-700 text-white' 
                                                    : 'bg-green-500 hover:bg-green-600 text-white'
                                            } transition-colors flex items-center gap-1`}
                                        >
                                            <FontAwesomeIcon icon={faEdit} />
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDeletePost(post._id)}
                                            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                                                darkMode 
                                                    ? 'bg-red-600 hover:bg-red-700 text-white' 
                                                    : 'bg-red-500 hover:bg-red-600 text-white'
                                            } transition-colors flex items-center gap-1`}
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {deleteConfirmation.show && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className={`p-6 rounded-2xl shadow-xl max-w-md w-full border ${
                        darkMode ? 'bg-gray-800/95 border-gray-700' : 'bg-white/95 backdrop-blur-md border-gray-200'
                    }`}>
                        <h3 className="text-2xl font-semibold mb-2">Confirm Delete</h3>
                        <p className="mb-2">Are you sure you want to delete this blog post?</p>
                        <p className="mb-6 font-semibold">{deleteConfirmation.postTitle}</p>
                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => setDeleteConfirmation({ show: false, postId: null, postTitle: '', isDeleting: false })}
                                className={`px-4 py-2 rounded ${
                                    darkMode ? 'bg-gray-600 hover:bg-gray-700 text-white' : 'bg-gray-300 hover:bg-gray-400 text-gray-800'
                                }`}
                                disabled={deleteConfirmation.isDeleting}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDelete}
                                disabled={deleteConfirmation.isDeleting}
                                className={`px-4 py-2 rounded text-white ${
                                    darkMode ? 'bg-red-600 hover:bg-red-700' : 'bg-red-500 hover:bg-red-600'
                                } disabled:bg-red-400 disabled:cursor-not-allowed flex items-center gap-2`}
                            >
                                {deleteConfirmation.isDeleting && <FontAwesomeIcon icon={faSpinner} className="animate-spin" />}
                                {deleteConfirmation.isDeleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

BlogManagement.propTypes = {
    darkMode: PropTypes.bool.isRequired,
    onEditPost: PropTypes.func
};

export default BlogManagement;
